import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X } from 'lucide-react';

const VIDEO_URL = 'https://file.skynet.re.kr/arescaper/f20260430_2cahq7.mp4';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.antsnest.argreatescape&pcampaignid=web_share';
const IOS_URL = 'https://apps.apple.com/kr/app/ar%EB%8C%80%ED%83%88%EC%B6%9C/id6759594948';

const qrSrc = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=6&data=${encodeURIComponent(url)}`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 30, 0.7);
  backdrop-filter: blur(6px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 22px;
  overflow: hidden;
  width: 100%;
  max-width: 1040px;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);

  @media (max-width: 480px) { border-radius: 14px; }
`;

const Body = styled.div`
  display: flex;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const VideoSection = styled.div`
  flex: 1.7;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  display: block;
  background: #000;
`;

const AppSection = styled.div`
  flex: 1;
  background: linear-gradient(165deg, #ffffff 0%, #f5f3ff 100%);
  padding: 32px 28px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 900px) {
    padding: 24px 22px 14px;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
`;

const Tagline = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #ffffff;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  padding: 5px 12px;
  border-radius: 999px;
  text-transform: uppercase;
`;

const Headline = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0 0;
  line-height: 1.25;
  letter-spacing: -0.4px;

  em {
    font-style: normal;
    background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SubCopy = styled.p`
  font-size: 13px;
  color: #475569;
  margin: 0;
  line-height: 1.5;
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, #e2e8f0, transparent);
  margin: 4px 0 2px;
`;

const SectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
  }
  span {
    font-size: 12px;
    color: #64748b;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
`;

const QRBox = styled.div`
  width: 92px;
  height: 92px;
  background: #ffffff;
  border-radius: 10px;
  padding: 5px;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  img { width: 100%; height: 100%; display: block; }
`;

const RowContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const Hint = styled.span`
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.3;
`;

const StoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  color: #ffffff;
  background: ${p => p.$bg || '#0f172a'};
  transition: filter 0.15s;
  cursor: pointer;
  &:hover { filter: brightness(1.1); }
  svg { width: 12px; height: 12px; }
`;

const AndroidIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
    <path d="M17.523 15.341a1.001 1.001 0 110-2.002 1.001 1.001 0 010 2.002zm-11.046 0a1.001 1.001 0 110-2.002 1.001 1.001 0 010 2.002zM17.09 7.937l1.638-2.838a.34.34 0 00-.588-.34l-1.66 2.874a10.41 10.41 0 00-4.48-1.002 10.41 10.41 0 00-4.48 1.002L5.86 4.76a.34.34 0 10-.588.34L6.91 7.937C3.845 9.587 1.737 12.556 1.5 16h21c-.237-3.444-2.345-6.413-5.41-8.063z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.85); }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 22px 16px;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
`;

const DontShowLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  input { width: 14px; height: 14px; cursor: pointer; accent-color: #7c3aed; }
`;

const DismissText = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #0f172a; }
`;

const PromoVideoPopup = ({ onClose }) => {
  const [dontShow, setDontShow] = useState(false);

  const handleClose = () => {
    if (dontShow) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const iso = tomorrow.toISOString();
      localStorage.setItem('promoVideoDontShowUntil', iso);
      localStorage.setItem('downloadPopupDontShowUntil', iso);
    }
    onClose?.();
  };

  return (
    <Backdrop onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Body>
          <VideoSection>
            <Video src={VIDEO_URL} autoPlay muted loop playsInline controls />
          </VideoSection>

          <AppSection>
            <HeroCopy>
              <Tagline>NEW · 모바일 앱 출시</Tagline>
              <Headline>
                어디서나 배우고,<br />
                즐겁게 푸는 <em>AR 대탈출</em>
              </Headline>
              <SubCopy>
                내가 만든 퀴즈를 AR로 체험하고,<br />
                친구들과 함께 풀어보세요.
              </SubCopy>
            </HeroCopy>

            <Divider />

            <SectionTitle>
              <strong>📱 모바일 앱 설치</strong>
              <span>QR을 스캔해 앱으로 사용하세요</span>
            </SectionTitle>

            <Row>
              <QRBox>
                <img src={qrSrc(ANDROID_URL)} alt="Android QR" />
              </QRBox>
              <RowContent>
                <Brand><AndroidIcon /> Android</Brand>
                <Hint>Google Play에서 AR대탈출 설치</Hint>
                <StoreButton
                  href={ANDROID_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  $bg="linear-gradient(135deg, #34a853, #0f9d58)"
                >
                  <AndroidIcon /> Google Play
                </StoreButton>
              </RowContent>
            </Row>

            <Row>
              <QRBox>
                <img src={qrSrc(IOS_URL)} alt="iOS QR" />
              </QRBox>
              <RowContent>
                <Brand><AppleIcon /> iPhone</Brand>
                <Hint>App Store에서 AR대탈출 설치</Hint>
                <StoreButton
                  href={IOS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  $bg="linear-gradient(135deg, #1f2937, #000000)"
                >
                  <AppleIcon /> App Store
                </StoreButton>
              </RowContent>
            </Row>
          </AppSection>
        </Body>

        <Footer>
          <DontShowLabel>
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
            />
            오늘 하루 보지 않기
          </DontShowLabel>
          <DismissText onClick={handleClose}>닫기</DismissText>
        </Footer>

        <CloseBtn onClick={handleClose} aria-label="닫기">
          <X size={18} />
        </CloseBtn>
      </Modal>
    </Backdrop>
  );
};

export default PromoVideoPopup;
